import { useState } from 'react'
import { useNavigate } from 'react-router'
import clsx from 'clsx'
import {
  Address as ModelsAddress,
  AddressIssue,
  AddressSuggestion
} from '@itsrever/returns-api-types'
import { Button } from '@heroui/react'
import { usePostHog } from '@posthog/react'
import { useTheme } from '@/ui/theme'
import { useAppSelector, useAppDispatch } from '@/redux/hooks'
import {
  setReturnAddress,
  RefundActions,
  setExchangeAddress
} from '@/redux/return/return-slice'
import ReverCard from '@/ui/components/rever-card'
import {
  useReturnMethods,
  useTranslate,
  usePages,
  useRefundMethods
} from '@/hooks'
import { LoadingOverlay } from '@/ui/components/loading-overlay'
import { HoffOrder } from '@/ui/custom/hoff-order'
import { AddressForm, AddressFormData } from '@/app/return-address/address-form'
import { AddressSuggestionModal } from '@/app/return-address/address-suggestion-modal'
import {
  ADDRESS_VALIDATION_FLAG_ID,
  useFeatureFlag
} from '@/hooks/use-feature-flag'
import {
  applyAddressIssues,
  applyAddressSuggestion,
  isFixable,
  pickIssues,
  pickSuggestions,
  useValidateAddress
} from '@/hooks/use-validate-address'

function ReturnAddress() {
  const { pages } = usePages()

  const { translate } = useTranslate()

  const theme = useTheme()

  const navigate = useNavigate()

  const dispatch = useAppDispatch()

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )

  const address = useAppSelector((store) => store.return.returnAddress)
  const setAddress = (address: ModelsAddress) => {
    dispatch(setReturnAddress(address))
    dispatch(setExchangeAddress(address))
  }

  const refundMethods = useAppSelector(
    (store) =>
      store.refundMethodsApi.refundMethods.response.result?.refund_methods
  )
  const provisionalRefundAmount = useAppSelector(
    (store) => store.refundMethodsApi.provisionalRefundAmount
  )
  const isKeepYourItemOffered = refundMethods?.some(
    (method) => method.keep_your_item?.enabled === true
  )

  const { callReturnMethods, loadingReturnMethods } = useReturnMethods(
    onSuccessReturnMethods
  )
  const { callRefundMethods, loadingRefundMethods } = useRefundMethods(
    onSuccessRefundMethods
  )

  const returnedItems = useAppSelector((store) => store.return.returnedItems)
  const onlyExchanges = returnedItems.every(
    (item) => item.item.action === RefundActions.ToExchange
  )

  function onSuccessRefundMethods() {
    if (
      (onlyExchanges && !isKeepYourItemOffered) ||
      (provisionalRefundAmount &&
        provisionalRefundAmount <= 0 &&
        !isKeepYourItemOffered)
    ) {
      callReturnMethods(address)
    } else {
      navigate(pages.RefundMethod)
    }
  }
  function onSuccessReturnMethods() {
    navigate(pages.ReturnMethod)
  }

  const posthog = usePostHog()
  const ecommerceID = settings.ecommerce_id
  const checkoutID = useAppSelector(
    (store) => store.returnsApi.order.response.checkout_id
  )
  const refundPaymentMethod = useAppSelector(
    (store) => store.return.refundPaymentMethod
  )
  const { isFlagEnabled: isAddressValidationEnabled } = useFeatureFlag(
    ADDRESS_VALIDATION_FLAG_ID
  )
  const validateAddress = useValidateAddress()
  const [pendingAddress, setPendingAddress] = useState<ModelsAddress | null>(
    null
  )
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [issues, setIssues] = useState<AddressIssue[]>([])
  const [suggestionCarrier, setSuggestionCarrier] = useState<string>()
  // The address the shopper chose to edit after a hint: submitting it unchanged
  // continues without asking again, so the modal can never loop.
  const [editedAfterHint, setEditedAfterHint] = useState<string>()

  const proceed = (addressData: ModelsAddress) => {
    setPendingAddress(null)
    setSuggestions([])
    setIssues([])
    setAddress(addressData)
    callRefundMethods(addressData)
  }

  // The check is a suggestion, never a gate: any failure or non-answer continues as today.
  const onClickContinue = (values: AddressFormData) => {
    const addressData = {
      ...values,
      country: values.country?.name,
      country_code: values.country?.code,
      state_province: values.province?.name,
      state_province_code: values.province?.code
    }

    if (
      !isAddressValidationEnabled ||
      !ecommerceID ||
      !checkoutID ||
      returnedItems.length === 0 ||
      JSON.stringify(addressData) === editedAfterHint
    ) {
      proceed(addressData)
      return
    }

    validateAddress.mutate(
      {
        ecommerceID,
        request: {
          checkout_id: checkoutID,
          return_address: addressData,
          items_to_return: returnedItems.map((item) => item.item),
          refund_method: refundPaymentMethod.method
        }
      },
      {
        onSuccess: (response) => {
          const found = pickSuggestions(response)
          const flagged = pickIssues(response)
          if (found.length === 0 && flagged.length === 0) {
            proceed(addressData)
            return
          }
          setPendingAddress(addressData)
          setSuggestions(found)
          setIssues(flagged)
          setSuggestionCarrier(response.carrier ?? flagged[0]?.carrier)
          posthog.capture('Address Suggestion Shown', {
            checkout_id: checkoutID,
            carrier: response.carrier ?? flagged[0]?.carrier,
            candidates: found.length,
            confidence: found[0]?.confidence,
            issues: flagged.map((issue) => issue.code),
            fixable_issues: flagged.filter(isFixable).length
          })
        },
        onError: () => proceed(addressData)
      }
    )
  }

  const onAcceptSuggestion = (
    suggestion: AddressSuggestion | undefined,
    fixes: AddressIssue[]
  ) => {
    if (!pendingAddress) return
    posthog.capture('Address Suggestion Accepted', {
      checkout_id: checkoutID,
      carrier: suggestionCarrier,
      confidence: suggestion?.confidence,
      locality: Boolean(suggestion),
      fixes: fixes.map((fix) => fix.field)
    })
    const withLocality = suggestion
      ? applyAddressSuggestion(pendingAddress, suggestion)
      : pendingAddress
    proceed(applyAddressIssues(withLocality, fixes))
  }

  const onKeepAddress = () => {
    if (!pendingAddress) return
    posthog.capture('Address Suggestion Dismissed', {
      checkout_id: checkoutID,
      carrier: suggestionCarrier,
      issues: issues.map((issue) => issue.code)
    })
    proceed(pendingAddress)
  }

  const onEditAddress = () => {
    if (!pendingAddress) return
    posthog.capture('Address Suggestion Edited', {
      checkout_id: checkoutID,
      carrier: suggestionCarrier,
      issues: issues.map((issue) => issue.code)
    })
    setEditedAfterHint(JSON.stringify(pendingAddress))
    setPendingAddress(null)
    setSuggestions([])
    setIssues([])
  }

  const isLoading =
    loadingRefundMethods || loadingReturnMethods || validateAddress.isPending
  const suggestionModal = pendingAddress ? (
    <AddressSuggestionModal
      isOpen
      address={pendingAddress}
      suggestions={suggestions}
      issues={issues}
      onAccept={onAcceptSuggestion}
      onKeep={onKeepAddress}
      onEdit={onEditAddress}
    />
  ) : null

  const logoUrl = settings.branding?.logo_url
  const title = settings.branding?.title
  const backgroundUrl = settings.branding?.background_image_url

  const isMisako = settings.ecommerce_group === 'misako'
  const isHoff = settings.ecommerce_group === 'hoff'

  if (isHoff) {
    return (
      <>
        {isLoading ? <LoadingOverlay loading /> : null}
        {suggestionModal}

        <div className="relative mx-auto flex size-full max-w-[1000px] grow flex-col items-center bg-cover bg-center bg-no-repeat px-4 md:px-10">
          <HoffOrder />
          <div className="size-full">
            <div className="mb-8 mt-6 w-full max-w-screen-lg">
              <div
                className="my-6 font-light"
                dangerouslySetInnerHTML={{
                  __html: translate('return_address_return_info')
                }}
              />
              <AddressForm address={address} onSubmit={onClickContinue} />
              <div className="mt-6 flex w-full">
                <button
                  form="address"
                  id="returnAddressContinue"
                  className="ml-auto mt-8 w-fit min-w-44 cursor-pointer rounded-xl bg-black p-4 text-sm text-white disabled:cursor-not-allowed disabled:bg-neutral-500"
                >
                  {translate('return_page_button_continue')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (isMisako) {
    return (
      <>
        {isLoading ? <LoadingOverlay loading /> : null}
        {suggestionModal}
        <div className="relative grid grid-cols-1 md:grid-cols-2">
          <div
            className={clsx(
              'sticky top-0 -z-10 h-screen w-full [grid-area:1/1] md:z-0 md:[grid-area:1/2]',
              {
                'bg-neutral-200': !backgroundUrl
              }
            )}
          >
            {backgroundUrl ? (
              <img
                src={backgroundUrl}
                className="size-full object-cover"
                alt=""
              />
            ) : null}
          </div>
          <div className="flex h-full flex-col gap-4 bg-white p-4 [grid-area:1/1] md:[grid-area:1/1] lg:px-10 lg:py-6">
            <div className="flex w-full items-end">
              {logoUrl ? (
                <div className="mr-auto">
                  <img
                    style={
                      {
                        '--logo-desktop-height': '1.5rem',
                        '--logo-mobile-height': '1.2rem'
                      } as React.CSSProperties
                    }
                    width={285}
                    height={70}
                    src={logoUrl}
                    alt={title}
                    className="h-[var(--logo-mobile-height)] w-auto object-contain lg:h-[var(--logo-desktop-height)]"
                  />
                </div>
              ) : null}
              <div className="flex items-center gap-5">
                <a
                  href="https://www.misako.com/pages/wishlist"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="https://www.misako.com/cdn/shop/files/favorite.svg?v=1731587455"
                    width={20}
                    height={20}
                  />
                </a>
                <a
                  href="https://www.misako.com/account"
                  target="_blank"
                  rel="noreferrer"
                >
                  <svg
                    width="20"
                    height="21"
                    viewBox="0 0 20 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19 20V17.7778C19 15.3222 16.9863 13.3333 14.5 13.3333H5.5C3.01375 13.3333 1 15.3222 1 17.7778V20"
                      stroke="rgb(21, 21, 21)"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                    <path
                      d="M10 9C12.2091 9 14 7.20914 14 5C14 2.79086 12.2091 1 10 1C7.79086 1 6 2.79086 6 5C6 7.20914 7.79086 9 10 9Z"
                      stroke="rgb(21, 21, 21)"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  </svg>
                </a>
                <a
                  href="https://www.misako.com/collections/top-ventas"
                  target="_blank"
                  rel="noreferrer"
                >
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.6969 13.9469L18 18.25M15.875 8.6875C15.875 12.7951 12.5451 16.125 8.4375 16.125C4.32988 16.125 1 12.7951 1 8.6875C1 4.57988 4.32988 1.25 8.4375 1.25C12.5451 1.25 15.875 4.57988 15.875 8.6875Z"
                      stroke="rgb(21, 21, 21)"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  </svg>
                </a>
              </div>
            </div>
            <div color="#ffffff" className="flex w-full flex-1 flex-col">
              <h1 className="mt-5 text-lg font-[300] tracking-wider text-neutral-600 lg:text-xl">
                Dirección
              </h1>
              <p
                className="mb-9 mt-2 text-[15px] font-[300] tracking-wide"
                dangerouslySetInnerHTML={{
                  __html: translate('return_address_return_info')
                }}
              />
              <AddressForm address={address} onSubmit={onClickContinue} />
              <div className="mt-auto flex items-center justify-between gap-4 pt-6">
                <div className="w-full md:max-w-[180px]">
                  <Button
                    onPress={() => navigate(-1)}
                    id="returnAddressContinue"
                    fullWidth
                    variant="bordered"
                    radius="sm"
                    style={{
                      borderColor: theme.colors.primary.main,
                      color: theme.colors.primary.main,
                      borderRadius: theme.borderRadius
                    }}
                  >
                    <span className="text-[13px] font-[300] uppercase tracking-wide">
                      Volver
                    </span>
                  </Button>
                </div>
                <div className="w-full md:max-w-[180px]">
                  <Button
                    type="submit"
                    form="address"
                    id="returnAddressContinue"
                    fullWidth
                    variant="solid"
                    radius="sm"
                    style={{
                      backgroundColor: theme.colors.primary.main,
                      color: theme.colors.primary.contrastText,
                      borderRadius: theme.borderRadius
                    }}
                  >
                    <span className="text-[13px] font-[300] uppercase tracking-wide">
                      {translate('return_page_button_continue')}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {isLoading ? <LoadingOverlay loading /> : null}
      {suggestionModal}
      <div className="flex flex-1 flex-col content-center items-center lg:bg-[#f2f2f2]">
        <ReverCard>
          <div
            className="mb-8 text-center font-normal [text-wrap:pretty]"
            dangerouslySetInnerHTML={{
              __html: translate('return_address_return_info')
            }}
          />
          <AddressForm address={address} onSubmit={onClickContinue} />
          <div className="mt-6 flex w-full justify-center">
            <Button
              type="submit"
              form="address"
              id="returnAddressContinue"
              fullWidth
              variant="solid"
              radius="sm"
              size="lg"
              style={{
                backgroundColor: theme.colors.primary.main,
                color: theme.colors.primary.contrastText,
                borderRadius: theme.borderRadius
              }}
            >
              {translate('return_page_button_continue')}
            </Button>
          </div>
        </ReverCard>
      </div>
    </>
  )
}
export default ReturnAddress
